const DESKTOP_QUERY = '(min-width: 990px)';
const GRID_SELECTOR = '.product-information__grid.product-information--media-left:not(.product-information--media-none)';
const MEDIA_CLASS = 'product-information__media';
const CONTENT_CLASS = 'product-information__content-column';
const INNER_SELECTOR = '.product-information__media-inner';
const GALLERY_SELECTOR = 'media-gallery';
const STICKY_GAP = 24;

const mediaQuery = window.matchMedia(DESKTOP_QUERY);
const controllers = new Map();
let ticking = false;

function readPixelVariable(name) {
  const rootValue = getComputedStyle(document.documentElement).getPropertyValue(name);
  const bodyValue = getComputedStyle(document.body).getPropertyValue(name);
  return Math.max(parseFloat(rootValue) || 0, parseFloat(bodyValue) || 0);
}

function getStickyOffset() {
  const headerVariableHeight = Math.max(readPixelVariable('--header-height'), readPixelVariable('--header-row-height'));
  const visibleHeader = document.querySelector('.header__row.is-sticky, .header.is-sticky');
  const visibleHeaderHeight = visibleHeader instanceof HTMLElement ? visibleHeader.getBoundingClientRect().height : 0;

  return Math.max(headerVariableHeight, visibleHeaderHeight) + STICKY_GAP;
}

function setImportantStyle(element, property, value) {
  element.style.setProperty(property, value, 'important');
}

function removeStyles(element, properties) {
  for (const property of properties) {
    element.style.removeProperty(property);
  }
}

function getDirectChildByClass(parent, className) {
  return Array.from(parent.children).find((child) => child.classList.contains(className));
}

class ProductMediaSticky {
  constructor(grid) {
    this.grid = grid;
    this.observedElements = new Set();
    this.resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleUpdate) : null;
  }

  get media() {
    return getDirectChildByClass(this.grid, MEDIA_CLASS);
  }

  get content() {
    return getDirectChildByClass(this.grid, CONTENT_CLASS);
  }

  get inner() {
    return this.media?.querySelector(INNER_SELECTOR);
  }

  get gallery() {
    return this.media?.querySelector(GALLERY_SELECTOR);
  }

  observeLayoutTargets(elements) {
    if (!this.resizeObserver) return;

    for (const element of elements) {
      if (!element || this.observedElements.has(element)) continue;

      this.resizeObserver.observe(element);
      this.observedElements.add(element);
    }
  }

  disconnect() {
    this.resizeObserver?.disconnect();
    this.observedElements.clear();
  }

  reset() {
    const media = this.media;
    const inner = this.inner;
    const gallery = this.gallery;

    if (media) {
      removeStyles(media, ['height', 'min-height']);
    }

    if (inner) {
      removeStyles(inner, ['height', 'min-height']);
    }

    if (gallery) {
      removeStyles(gallery, ['position', 'top', 'right', 'bottom', 'left', 'width', 'height', 'min-height', 'z-index']);
    }
  }

  update() {
    if (!this.grid.isConnected) {
      this.reset();
      this.disconnect();
      controllers.delete(this.grid);
      return;
    }

    const media = this.media;
    const content = this.content;
    const inner = this.inner;
    const gallery = this.gallery;

    if (!media || !content || !inner || !gallery || !mediaQuery.matches) {
      this.reset();
      return;
    }

    this.observeLayoutTargets([this.grid, media, content, inner, gallery]);

    const boundaryHeight = Math.max(content.offsetHeight, gallery.offsetHeight);
    const galleryHeight = gallery.offsetHeight;

    if (!boundaryHeight || !galleryHeight || boundaryHeight <= galleryHeight) {
      this.reset();
      return;
    }

    const offset = getStickyOffset();
    const scrollY = window.scrollY || window.pageYOffset;
    const gridRect = this.grid.getBoundingClientRect();
    const mediaRect = media.getBoundingClientRect();
    const mediaStyles = getComputedStyle(media);
    const paddingLeft = parseFloat(mediaStyles.paddingLeft) || 0;
    const paddingRight = parseFloat(mediaStyles.paddingRight) || 0;
    const contentLeft = mediaRect.left + paddingLeft;
    const contentWidth = Math.max(mediaRect.width - paddingLeft - paddingRight, 0);
    const boundaryTop = gridRect.top + scrollY;
    const boundaryBottom = boundaryTop + boundaryHeight;
    const stickyTop = scrollY + offset;
    const bottomLimit = boundaryBottom - galleryHeight;

    setImportantStyle(media, 'height', `${boundaryHeight}px`);
    setImportantStyle(media, 'min-height', `${boundaryHeight}px`);
    setImportantStyle(inner, 'height', `${boundaryHeight}px`);
    setImportantStyle(inner, 'min-height', `${boundaryHeight}px`);

    if (stickyTop <= boundaryTop) {
      setImportantStyle(gallery, 'position', 'static');
      removeStyles(gallery, ['top', 'right', 'bottom', 'left', 'width', 'height', 'min-height', 'z-index']);
      return;
    }

    if (stickyTop >= bottomLimit) {
      setImportantStyle(gallery, 'position', 'absolute');
      setImportantStyle(gallery, 'top', `${Math.max(boundaryHeight - galleryHeight, 0)}px`);
      setImportantStyle(gallery, 'left', '0px');
      setImportantStyle(gallery, 'width', '100%');
      setImportantStyle(gallery, 'z-index', 'var(--layer-flat)');
      removeStyles(gallery, ['right', 'bottom']);
      return;
    }

    setImportantStyle(gallery, 'position', 'fixed');
    setImportantStyle(gallery, 'top', `${offset}px`);
    setImportantStyle(gallery, 'left', `${contentLeft}px`);
    setImportantStyle(gallery, 'width', `${contentWidth}px`);
    setImportantStyle(gallery, 'z-index', 'var(--layer-flat)');
    removeStyles(gallery, ['right', 'bottom']);
  }
}

function syncControllers() {
  const grids = document.querySelectorAll(GRID_SELECTOR);

  for (const grid of grids) {
    if (!controllers.has(grid)) {
      controllers.set(grid, new ProductMediaSticky(grid));
    }
  }

  for (const grid of controllers.keys()) {
    if (!grid.isConnected) {
      controllers.delete(grid);
    }
  }
}

function updateAll() {
  syncControllers();

  for (const controller of controllers.values()) {
    controller.update();
  }
}

function scheduleUpdate() {
  if (ticking) return;

  ticking = true;
  requestAnimationFrame(() => {
    ticking = false;
    updateAll();
  });
}

function initProductMediaSticky() {
  updateAll();

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate);
  window.addEventListener('load', scheduleUpdate);
  document.addEventListener('shopify:section:load', scheduleUpdate);
  document.addEventListener('shopify:section:reorder', scheduleUpdate);
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', scheduleUpdate);
  } else {
    mediaQuery.addListener(scheduleUpdate);
  }

  const observer = new MutationObserver(scheduleUpdate);
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductMediaSticky, { once: true });
} else {
  initProductMediaSticky();
}
